import React, { useState, useEffect } from 'react'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import './DepartmentEmployeesImprovementPlans.css'
import axios from 'axios'
import Cookies from 'js-cookie'
import { FaSearch, FaFilter, FaEye, FaEdit, FaCalendarAlt, FaUser, FaCheckCircle, FaTimesCircle, FaClock, FaCommentDots, FaSpinner } from 'react-icons/fa'
import { toast } from 'react-toastify'
import API_BASE_URL from '../../../config/api'

const DepartmentEmployeesImprovementPlans = () => {
  const [improvementPlans, setImprovementPlans] = useState([])
  const [filteredPlans, setFilteredPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedYear, setSelectedYear] = useState('')
  const [availableYears, setAvailableYears] = useState([])
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [approveOpen, setApproveOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [supervisorComment, setSupervisorComment] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')

  useEffect(() => {
    fetchImprovementPlans()
  }, [])

  useEffect(() => {
    filterPlans()
  }, [improvementPlans, searchQuery, selectedYear])

  const fetchImprovementPlans = async () => {
    try {
      setLoading(true)
      const token = Cookies.get('token')
      const response = await axios.get(`${API_BASE_URL}/api/improvement-plans/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.data && response.data.length > 0) {
        setImprovementPlans(response.data)
        
        // Extract unique years and sort them, filtering out null/undefined values
        const years = [...new Set(response.data
          .map(plan => plan.year)
          .filter(year => year !== null && year !== undefined)
        )].sort((a, b) => b - a)
        setAvailableYears(years)
        
        // Set the most recent year as default
        if (years.length > 0) {
          setSelectedYear(years[0].toString())
        }
      } else {
        setImprovementPlans([])
      }
    } catch (error) {
      console.error('Error fetching improvement plans:', error)
      setError('Failed to fetch improvement plans')
    } finally {
      setLoading(false)
    }
  }

  const filterPlans = () => {
    let filtered = [...improvementPlans]

    // Filter by year
    if (selectedYear) {
      filtered = filtered.filter(plan => 
        plan.year !== null && 
        plan.year !== undefined && 
        plan.year.toString() === selectedYear
      )
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(plan => 
        (plan.employee_name && plan.employee_name.toLowerCase().includes(query)) ||
        (plan.employee_email && plan.employee_email.toLowerCase().includes(query)) ||
        (plan.strengths && plan.strengths.toLowerCase().includes(query)) ||
        (plan.weaknesses && plan.weaknesses.toLowerCase().includes(query)) ||
        (plan.employee_comment && plan.employee_comment.toLowerCase().includes(query)) ||
        (plan.training_needs && plan.training_needs.toLowerCase().includes(query)) ||
        (plan.supervisor_comment && plan.supervisor_comment.toLowerCase().includes(query))
      )
    }

    setFilteredPlans(filtered)
  }

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedPlan(null)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const splitToList = (value) => {
    if (!value) return []
    if (Array.isArray(value)) return value.filter(Boolean)
    return String(value)
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
  }

  const updatePlanStatus = async (planId, status, extra = {}) => {
    try {
      setActionLoading(true)
      const token = Cookies.get('token')
      await axios.patch(`${API_BASE_URL}/api/improvement-plans/${planId}/`, {
        approval_status: status,
        ...extra
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      toast.success(`Plan ${status}`)
      await fetchImprovementPlans()
      // Update currently selected plan status in modal
      setSelectedPlan(prev => prev ? { ...prev, approval_status: status, ...extra } : prev)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status')
    } finally {
      setActionLoading(false)
    }
  }

  const handleApprove = () => {
    if (!selectedPlan?.id) return
    setSupervisorComment('')
    setApproveOpen(true)
  }

  const handleReject = () => {
    if (!selectedPlan?.id) return
    setRejectionReason('')
    setRejectOpen(true)
  }

  const submitApprove = async () => {
    if (!selectedPlan?.id) return
    await updatePlanStatus(selectedPlan.id, 'approved', { supervisor_comment: supervisorComment })
    setApproveOpen(false)
  }

  const submitReject = async () => {
    if (!selectedPlan?.id) return
    await updatePlanStatus(selectedPlan.id, 'rejected', { rejection_reason: rejectionReason })
    setRejectOpen(false)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle className="status-icon approved" />
      case 'rejected':
        return <FaTimesCircle className="status-icon rejected" />
      case 'pending':
        return <FaClock className="status-icon pending" />
      default:
        return <FaClock className="status-icon pending" />
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved'
      case 'rejected':
        return 'status-rejected'
      case 'pending':
        return 'status-pending'
      default:
        return 'status-pending'
    }
  }

  if (loading) {
    return (
      <>
      <Topbar/>
      <Sidebar/>
      <div className="employee-goals-container">
        <div className="loading-container">
          <FaSpinner className="spinner" />
          <p>Loading improvement plans...</p>
        </div>
      </div>
      </>
      
    )
  }

  if (error) {
    return (
      <>
      <Topbar/>
      <Sidebar/>
      <div className="employee-goals-container">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchImprovementPlans} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
      </>
      
    )
  }

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-improvement-plans-container">
      <h2 style={{color: '#170d81', marginTop: '-15px'}}>Employee Improvement Plans</h2>
        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by employee name, email, or content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-container">
            <FaFilter className="filter-icon" />
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="year-filter"
            >
              <option value="">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="results-summary">
          <p>
            Showing {filteredPlans.length} improvement plan{filteredPlans.length !== 1 ? 's' : ''}
            {selectedYear && ` for ${selectedYear}`}
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>

        {/* Improvement Plans Table */}
        {filteredPlans.length === 0 ? (
          <div className="no-data-container">
            <div className="no-data-icon">
              <FaCommentDots />
            </div>
            <h3>No Improvement Plans Found</h3>
            <p>
              {searchQuery || selectedYear 
                ? 'Try adjusting your search criteria or filters.'
                : 'No improvement plans have been submitted yet.'
              }
            </p>
          </div>
        ) : (
          <div className="table-container">
            <table className="improvement-plans-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Year</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlans.map((plan) => (
                  <tr key={plan.id} className="table-row">
                    <td className="employee-cell">
                      <div className="employee-info">
                        <FaUser className="employee-icon" />
                        <div className="employee-details">
                          <div className="employee-name">{plan.employee_name || 'Unknown Employee'}</div>
                          <div className="employee-email">{plan.employee_email || 'No email provided'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="year-cell">
                      <div className="year-badge">
                        <FaCalendarAlt />
                        <span>{plan.year || 'N/A'}</span>
                      </div>
                    </td>
                    
                    <td className="status-cell">
                      <div className={`status-badge ${getStatusClass(plan.approval_status)}`}>
                        {getStatusIcon(plan.approval_status)}
                        <span className="status-text">
                          {plan.approval_status === 'approved' ? 'Approved' :
                           plan.approval_status === 'rejected' ? 'Rejected' : 'Pending'}
                        </span>
                      </div>
                    </td>
                    
                    <td className="actions-cell">
                      <button
                        onClick={() => handleViewPlan(plan)}
                        className="view-btn"
                        title="View full details"
                      >
                        <FaEye />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal for viewing plan details */}
        {modalOpen && selectedPlan && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Improvement Plan Details</h2>
                <button onClick={closeModal} className="close-btn">&times;</button>
              </div>

              <div className="modal-body">
                <div className="modal-sections">
                  <div className="modal-section">
                    <h4>Strengths</h4>
                    {splitToList(selectedPlan.strengths).length > 0 ? (
                      <ul className="bullet-list">
                        {splitToList(selectedPlan.strengths).map((item, idx) => (
                          <li key={`s-${idx}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No strengths listed</p>
                    )}
                  </div>

                  <div className="modal-section">
                    <h4>Weaknesses</h4>
                    {splitToList(selectedPlan.weaknesses).length > 0 ? (
                      <ul className="bullet-list">
                        {splitToList(selectedPlan.weaknesses).map((item, idx) => (
                          <li key={`w-${idx}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No weaknesses listed</p>
                    )}
                  </div>

                  <div className="modal-section">
                    <h4>Training Needs</h4>
                    {splitToList(selectedPlan.training_needs).length > 0 ? (
                      <ul className="bullet-list">
                        {splitToList(selectedPlan.training_needs).map((item, idx) => (
                          <li key={`t-${idx}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No training needs specified</p>
                    )}
                  </div>

                  <div className="modal-section">
                    <h4>Employee Comment</h4>
                    <p className="comment-block">{selectedPlan.employee_comment || 'No comment provided'}</p>
                  </div>

                  {(selectedPlan.approval_status === 'approved' || selectedPlan.approval_status === 'rejected') && (
                    <div className="modal-section">
                      <h4>Supervisor Comment</h4>
                      <p>{selectedPlan.supervisor_comment || 'No supervisor comment provided'}</p>
                    </div>
                  )}

                  <div className="modal-section">
                    {selectedPlan.approval_status === 'rejected' && selectedPlan.rejection_reason && (
                      <div className="rejection-reason">
                        <FaCommentDots className="reason-icon" />
                        <p>{selectedPlan.rejection_reason}</p>
                      </div>
                    )}
                  </div>

                  {selectedPlan.date_signed && (
                    <div className="modal-section">
                      <h4>Date Signed</h4>
                      <p>{formatDate(selectedPlan.date_signed)}</p>
                    </div>
                  )}
                </div>
                {selectedPlan?.approval_status === 'pending' && (
                  <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
                    <button onClick={handleReject} disabled={actionLoading} className="btn btn-danger">Reject</button>
                    <button onClick={handleApprove} disabled={actionLoading} className="btn btn-primary">Approve</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {approveOpen && (
          <div className="modal-overlay" onClick={() => setApproveOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Approve Improvement Plan</h2>
                <button onClick={() => setApproveOpen(false)} className="close-btn">&times;</button>
              </div>
              <div className="modal-body">
                <div className="modal-section">
                  <h4>Supervisor Comment (optional)</h4>
                  <textarea
                    value={supervisorComment}
                    onChange={(e) => setSupervisorComment(e.target.value)}
                    placeholder="Enter supervisor comment..."
                    rows={1}
                    className="modal-textarea"
                  />
                </div>
                <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
                  <button onClick={() => setApproveOpen(false)} className="btn btn-secondary">Cancel</button>
                  <button onClick={submitApprove} disabled={actionLoading} className="btn btn-primary">Confirm Approve</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {rejectOpen && (
          <div className="modal-overlay" onClick={() => setRejectOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reject Improvement Plan</h2>
                <button onClick={() => setRejectOpen(false)} className="close-btn">&times;</button>
              </div>
              <div className="modal-body">
                <div className="modal-section">
                  <h4>Rejection Reason</h4>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Provide the reason for rejection..."
                    rows={1}
                    className="modal-textarea"
                  />
                </div>
                <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
                  <button onClick={() => setRejectOpen(false)} className="btn btn-secondary">Cancel</button>
                  <button onClick={submitReject} disabled={actionLoading || !rejectionReason.trim()} className="btn btn-danger">Confirm Reject</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default DepartmentEmployeesImprovementPlans